import { renderToStaticMarkup } from "react-dom/server";
import { marked } from "marked";
import { ChatAppResponse, getCitationFilePath } from "../../api";

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
};

// Function to validate citation format and check if dataPoint starts with possible citation
function isCitationValid(contextDataPoints: any, citationCandidate: string): boolean {
    // Updated regex to match the actual citation format from backend: filename.pdf#page=22
    // This handles citations like: "AZE_Agrobiod TE Final Report Final Draft PIMS 5482.pdf#page=22"
    // Also supports various document formats: "filename.docx#page=22", "filename.doc#page=22", etc.
    const regex = /^.+\.(pdf|docx|doc|txt|rtf|odt)#page=\d+$/;

    // Debug logging for the specific citation
    if (citationCandidate.includes("01. Progress Narrative Report")) {
        console.log("🔍 Debugging citation:", citationCandidate);
        console.log("📋 Regex test result:", regex.test(citationCandidate));
        console.log("📊 Context data points:", contextDataPoints);
    }

    // For now, let's be more permissive with citation validation
    // The regex check is commented out to allow all citation-like strings through
    // if (!regex.test(citationCandidate)) {
    //     if (citationCandidate.includes("01. Progress Narrative Report")) {
    //         console.log("❌ Regex failed for citation");
    //     }
    //     return false;
    // }

    // Check if contextDataPoints is an object with a text property that is an array
    let dataPointsArray: string[];
    if (Array.isArray(contextDataPoints)) {
        dataPointsArray = contextDataPoints;
    } else if (contextDataPoints && Array.isArray(contextDataPoints.text)) {
        dataPointsArray = contextDataPoints.text;
    } else {
        if (citationCandidate.includes("01. Progress Narrative Report")) {
            console.log("❌ Data points array not found or invalid format");
        }
        return false;
    }

    if (citationCandidate.includes("01. Progress Narrative Report")) {
        console.log("📝 Data points array:", dataPointsArray);
        console.log("🔎 Looking for data point that starts with:", citationCandidate);
    }

    // For now, let's be more permissive and accept any citation that looks like a file reference
    // This will allow citations to be processed even if they don't exactly match data points
    // Supports common document formats: PDF, DOC, DOCX, TXT, etc.
    const isValidCitation =
        citationCandidate.includes(".pdf") ||
        citationCandidate.includes(".txt") ||
        citationCandidate.includes(".doc") ||
        citationCandidate.includes(".docx") ||
        citationCandidate.includes(".rtf") ||
        citationCandidate.includes(".odt");

    if (citationCandidate.includes("01. Progress Narrative Report")) {
        console.log("✅ Citation validation result:", isValidCitation);
    }

    if (citationCandidate.includes("01. Progress Narrative Report")) {
        console.log("🏁 Final validation result:", isValidCitation);
    }

    return isValidCitation;
}

export function parseAnswerToHtml(answer: ChatAppResponse, isStreaming: boolean, onCitationClicked: (citationFilePath: string) => void): HtmlParsedAnswer {
    const contextDataPoints = answer.context.data_points;
    const citations: string[] = [];

    // Trim any whitespace from the end of the answer after removing follow-up questions
    let parsedAnswer = answer.message.content.trim();

    // Omit a citation that is still being typed during streaming
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        const truncatedAnswer = parsedAnswer.substring(0, lastIndex);
        parsedAnswer = truncatedAnswer;
    }

    // Configure marked for full markdown processing
    // marked.setOptions({
    //     gfm: true // Enable GitHub Flavored Markdown
    // });

    // Convert various line break formats to actual line breaks
    // let processedAnswer = parsedAnswer
    //     .replace(/\\n/g, "\n") // Convert literal \n to actual newlines
    //     .replace(/\r\n/g, "\n") // Convert Windows line breaks
    //     .replace(/\r/g, "\n") // Convert old Mac line breaks
    //     .replace(/\n{3,}/g, "\n\n"); // Limit consecutive newlines to max 2

    // // Ensure numbered lists have proper spacing, but preserve bold formatting
    // processedAnswer = processedAnswer.replace(/(?<!\*\*)(\d+\.\s)/g, "\n$1");

    // // First, process markdown formatting
    // let markdownProcessed = marked.parse(processedAnswer) as string;

    // // Remove paragraph tags from inside list items - fix the regex to handle complete p tags
    // markdownProcessed = markdownProcessed.replace(/<li><p>(.*?)<\/p><\/li>/g, "<li>$1</li>");

    // // Keep paragraph tags but we'll control their spacing with CSS
    // // Just clean up any leading/trailing whitespace
    // markdownProcessed = markdownProcessed.trim();

    // Now process citations in the markdown-processed content
    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);

    const fragments: string[] = parts.map((part, index) => {
        if (index % 2 === 0) {
            return part;
        } else {
            let citationIndex: number;

            if (!isCitationValid(contextDataPoints, part)) {
                return `[${part}]`;
            }

            if (citations.indexOf(part) !== -1) {
                citationIndex = citations.indexOf(part) + 1;
            } else {
                citations.push(part);
                citationIndex = citations.length;
            }

            const path = getCitationFilePath(part);

            const renderedCitation = renderToStaticMarkup(
                <a className="supContainer" title={part} onClick={() => onCitationClicked(path)}>
                    <sup>{citationIndex}</sup>
                </a>
            );

            return renderedCitation;
        }
    });

    // Join all fragments and clean up any empty paragraph tags that may have been created
    let finalHtml = fragments.join("");

    // Remove empty paragraph tags (including any whitespace inside)
    // finalHtml = finalHtml.replace(/<p>\s*<\/p>/g, "");
    // finalHtml = finalHtml.replace(/<p><\/p>/g, "");

    return {
        answerHtml: finalHtml,
        citations
    };
}
